import { useState } from 'react'
import { useAgentPin, useUpdateAgentPin } from '../../api/agent'

const PIN_RE = /^\d{4,12}$/
const inputCls =
  'rounded border border-gray-300 bg-white px-2 py-1 font-mono text-sm dark:border-gray-700 dark:bg-gray-900'
const btnCls =
  'rounded border border-gray-300 px-3 py-1 text-sm dark:border-gray-700 disabled:opacity-50'

/**
 * Admin control for the global agent UI-lock PIN. Lives in SettingsPage but is
 * its own component so the server mutation stays isolated from the page's
 * localStorage-backed preference toggles.
 */
export function AgentPinSection() {
  const { data, isLoading } = useAgentPin()
  const update = useUpdateAgentPin()

  // Seed the input from the loaded value once per server value (render-seed
  // pattern, per EditAccessKeyDialog — avoids a cascading useEffect+setState).
  const serverPin = data?.pin ?? ''
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  const [pin, setPin] = useState('')
  const loadedKey = data ? `${data.updated_at ?? ''}|${serverPin}` : null
  if (data && loadedKey !== hydratedFor) {
    setHydratedFor(loadedKey)
    setPin(serverPin)
  }

  const valid = pin === '' || PIN_RE.test(pin)

  if (isLoading) return <div className="text-sm text-gray-500">Loading…</div>

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Visual lock for the agent app — one value for all phones. It only hides
        the interface from a bystander; the agent keeps running in the
        background. Applies on each agent's next reconnect (≤5 min). Shown in
        plaintext so you can dictate it to operators.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, '').slice(0, 12))
            // Clear the stale "Saved."/"Failed." banner the moment the admin
            // edits the field — otherwise the confirmation lingers next to an
            // unsaved value, and this PIN is dictated to operators verbatim.
            if (update.isSuccess || update.isError) update.reset()
          }}
          placeholder="No PIN set"
          inputMode="numeric"
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => update.mutate(pin === '' ? null : pin)}
          disabled={!valid || update.isPending || pin === serverPin}
          className={btnCls}
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setPin('')
            update.mutate(null)
          }}
          disabled={update.isPending || !data?.pin}
          className={btnCls}
        >
          Clear
        </button>
      </div>
      {!valid && <p className="text-xs text-red-500">PIN must be 4–12 digits.</p>}
      {update.isError && <p className="text-xs text-red-500">Failed to save.</p>}
      {update.isSuccess && <p className="text-xs text-green-600">Saved.</p>}
      {data?.updated_at && (
        <p className="text-xs text-gray-400">
          Last updated {new Date(data.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  )
}
